import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { FaSpinner } from 'react-icons/fa';

const RetirementCalculator = () => {
  const [userContribution, setUserContribution] = useState('');
  const [interestRate, setInterestRate] = useState('');
  const [userAge, setUserAge] = useState('');
  const [results, setResults] = useState(null);
  const [investmentType, setInvestmentType] = useState('');
  const [historicalData, setHistoricalData] = useState([]);
  const [showHistorical, setShowHistorical] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // Function to format input value with commas
  const formatInput = (value) => {
    if (!value) return '';
    const num = parseFloat(value.replace(/,/g, '')); // Remove commas to parse
    if (isNaN(num)) return '';
    return num.toLocaleString('en-US');
  };

  const handleContributionChange = (e) => {
    const rawValue = e.target.value.replace(/,/g, ''); // Get raw number
    setUserContribution(rawValue);
  };

  const handleInterestChange = (e) => {
    const rawValue = e.target.value.replace(/,/g, ''); // Get raw number
    setInterestRate(rawValue);
  };

  const handleAgeChange = (e) => {
    const rawValue = e.target.value.replace(/,/g, ''); // Get raw number
    setUserAge(rawValue);
  };

  const calculateForRetirement = async () => {
    if (!userContribution || !interestRate || !userAge) {
      toast.error('Please enter all fields.');
      return;
    }
    const contribution = parseFloat(userContribution);
    const interest = parseFloat(interestRate) / 100;
    const age = parseInt(userAge);
    if (
      isNaN(contribution) ||
      isNaN(interest) ||
      isNaN(age) ||
      contribution <= 0 ||
      interest < 0 ||
      age < 0 ||
      age > 120
    ) {
      toast.error(
        'Please enter valid numbers (contribution > 0, interest >= 0, age 0-120).'
      );
      return;
    }

    const retirementAge = 65;
    const yearsUntilRetirement = retirementAge - age;
    if (yearsUntilRetirement <= 0) {
      toast.error('Age must be less than 65 for retirement calculation.');
      return;
    }

    let totalUserStash = 0;
    let totalInterest = 0;
    for (let year = 1; year <= yearsUntilRetirement; year++) {
      totalUserStash += contribution;
      const interestForThisYear = (totalUserStash + totalInterest) * interest;
      totalInterest += interestForThisYear;
    }

    setResults({
      contribution: totalUserStash.toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }),
      interest: totalInterest.toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }),
      total: (totalUserStash + totalInterest).toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }),
    });

    if (investmentType) {
      setIsLoading(true);
      try {
        const response = await axios.get(
          `https://www.alphavantage.co/query?function=TIME_SERIES_MONTHLY&symbol=${investmentType}&apikey=${process.env.REACT_APP_ALPHA_KEY}`
        );
        const data = response.data['Monthly Time Series'];
        const dates = Object.keys(data).slice(0, 12).reverse();
        const historicalData = [];
        for (let i = 0; i < dates.length - 1; i++) {
          const currentClose = parseFloat(data[dates[i]]['4. close']);
          const nextClose = parseFloat(data[dates[i + 1]]['4. close']);
          const ror = ((nextClose - currentClose) / currentClose) * 100;
          historicalData.push({ date: dates[i], ror: ror.toFixed(2) + '%' });
        }
        setHistoricalData(historicalData);
      } catch (error) {
        toast.error('Error fetching historical data.');
      } finally {
        setIsLoading(false);
      }
    }
  };

  const toggleCollapse = () => setResults(results ? null : results);
  const toggleHistorical = () => setShowHistorical(!showHistorical);

  return (
    <div className="calculator-section">
      <h2>Retirement Calculator</h2>
      <div style={{ display: 'flex', gap: '20px' }}>
        <div className="input-group">
          <label>Annual Contribution ($):</label>
          <input
            type="text"
            value={formatInput(userContribution)}
            onChange={handleContributionChange}
            placeholder="Enter contribution"
          />
          <label>Interest Rate (%):</label>
          <input
            type="text"
            value={formatInput(interestRate)}
            onChange={handleInterestChange}
            placeholder="Enter rate (0-100)"
          />
          <label>Current Age:</label>
          <input
            type="text"
            value={formatInput(userAge)}
            onChange={handleAgeChange}
            placeholder="Enter age"
          />
          <label>Investment Type (e.g., SPY):</label>
          <input
            type="text"
            value={investmentType}
            onChange={(e) => setInvestmentType(e.target.value)}
            placeholder="Enter ticker"
          />
          <button
            onClick={calculateForRetirement}
            disabled={!userContribution || !interestRate || !userAge}
          >
            Calculate
          </button>
        </div>
        <div className={`results-card ${!results ? 'collapsed' : ''}`}>
          {results && (
            <>
              <button className="collapse-btn" onClick={toggleCollapse}>
                X
              </button>
              <table>
                <thead>
                  <tr>
                    <th>Metric</th>
                    <th>Value ($)</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>Total Contribution</td>
                    <td>${results.contribution}</td>
                  </tr>
                  <tr>
                    <td>Total Interest</td>
                    <td>${results.interest}</td>
                  </tr>
                  <tr>
                    <td>Contribution + Interest</td>
                    <td>${results.total}</td>
                  </tr>
                </tbody>
              </table>
              {investmentType && (
                <button onClick={toggleHistorical}>
                  {showHistorical
                    ? 'Hide Historical RoR'
                    : 'Show Historical RoR'}
                </button>
              )}
              {showHistorical && historicalData.length > 0 && (
                <div>
                  <h3>Historical Rate of Return</h3>
                  <ul>
                    {historicalData.map((data, index) => (
                      <li key={index}>
                        {data.date}: {data.ror}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {isLoading && <FaSpinner className="loading" />}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default RetirementCalculator;
